import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { TableData } from "@/App/Marketplace/Table";
import { getMarketplaceSales } from "@/Api/marketplace";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { AddBtn } from "@/App/Marketplace/Add";
import { Store } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export type TableDataProps = {
  id: number;
  uuid: string;
  unity_id: number;
  client_id: number | null;
  sale_type_id: number;
  seller_id: number | null;
  circuit_route_id: number | null;
  tenant_id: number;
  total: string;
  total_commission_by_quote: number;
  sale_status_id: number;
  bank_account_id: number | null;
  sale_code_transaction: string | null;
  document: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  unity: {
    id: number;
    name: string;
  };
  client?: {
    id: number;
    name: string;
  } | null;
  seller?: {
    id: number;
    name: string;
  } | null;
  sale_type: {
    id: number;
    name: string;
  };
  created_by: {
    id: number;
    name: string;
  };
  updated_by?: {
    id: number;
    name: string;
  } | null;
  products: Array<{
    id: number;
    contest_id: number;
    lottery_id: number;
    product_name: string;
    product_classification_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
    pivot: {
      sale_id: number;
      contest_product_classification_id: number;
      quantity: number;
      price_unit: string;
      price_total: string;
      created_at: string;
      updated_at: string;
    };
    product_classification: {
      id: number;
      uuid: string;
      lottery_id: number;
      name: string;
      game_quantity: number;
      dez_quantity: string;
      quota_quantity: number;
      total_without_tariff: number;
      total_with_tariff: number;
      game_percentage: number;
      tariff: number;
      sale_price: number;
      price_without_tariff: number;
      commission_by_quote: number;
      image: string | null;
      created_by_id: number;
      is_active: number;
      product_type_id: number;
      tenant_id: number;
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
    };
    contest: {
      id: number;
      name: string;
      validation_date: string;
      lottery_id: number;
      lottery: {
        id: number;
        name: string;
        color: string;
      };
    };
  }>;
  transaction?: any;
  sale_status: {
    id: number;
    name: string;
  };
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [sortConfig, setSortConfig] = useState({
    column: "client",
    direction: "desc",
  });

  const user = useAuth();
  const { hasPermission } = usePermissions();

  const fetchData = async (page = 1, customParams = {}) => {
    setState("loading");
    try {
      const params = {
        seller_id: user?.user?.seller?.id,
        order_by: sortConfig.column,
        order_direction: sortConfig.direction,
        page,
        ...customParams,
      };

      const { data } = await getMarketplaceSales(params);
      setListData(data?.response);
      setState("production");
    } catch (error) {
      console.error("Erro ao carregar marketplace:", error);
      setState("production");
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.user?.seller?.id, sortConfig]);

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(listData?.current_page + 1);
      }
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(listData?.current_page - 1);
      }
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(listData?.last_page);
      }
    },
  };

  const handleSort = async (column: string) => {
    const direction =
      sortConfig.column === column && sortConfig.direction === "asc"
        ? "desc"
        : "asc";

    setSortConfig({ column, direction });
  };

  const refreshData = () => {
    fetchData(1);
  };

  return (
    <Main>
      <If condition={hasPermission("view-sales-marketplace-list")}>
        <PageHeader
          icon={Store}
          title="Marketplace"
          description="Vendas do marketplace"
        >
          <If condition={hasPermission("create-sales-marketplace")}>
            <AddBtn updateFront={refreshData} />
          </If>
        </PageHeader>

        <div className="flex flex-col gap-3">
          {state === "loading" ? (
            <CreateLoaderTable cols={6} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRefresh={refreshData}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
